// إدارة الجدارات — إنشاء وتعديل وترتيب وتحديد الجمهور المستهدف
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { errText } from "@/v2/lib/hooks";
import {
  GlassCard, Chip, Skeleton, Modal, PageHead, Confirm, Empty, AudiencePicker, AudienceChips,
} from "@/v2/admin/ui";
import {
  Target, Plus, Pencil, Trash2, ChevronUp, ChevronDown, Wrench, HeartHandshake, Crown, Sparkles, Layers,
} from "lucide-react";

const ICONS = { Target, Wrench, HeartHandshake, Crown, Sparkles, Layers };
const ICON_KEYS = Object.keys(ICONS);
const STATUS = { active: "مفعّلة", inactive: "معطّلة", archived: "مؤرشفة" };

const empty = { name: "", desc: "", icon: "Target", image: "", audience: [], status: "active" };

function CompModal({ item, onClose, onSaved }) {
  const [f, setF] = useState(item ? { ...empty, ...item } : empty);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.name.trim().length < 3) return toast.error("اسم الجدارة مطلوب");
    setBusy(true);
    try {
      const body = { name: f.name.trim(), desc: f.desc, icon: f.icon, image: f.image, audience: f.audience, status: f.status, cascade_audience: true };
      if (item) await api.patch(`/admin/competencies/${item.id}`, body);
      else await api.post("/admin/competencies", body);
      toast.success(item ? "تم تحديث الجدارة" : "تم إنشاء الجدارة");
      onSaved(); onClose();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={item ? "تعديل جدارة" : "جدارة جديدة"} onClose={onClose} testId="comp-modal">
      <div className="space-y-4">
        <div><label className="v2-label">اسم الجدارة</label>
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="comp-name" className="v2-input" /></div>
        <div><label className="v2-label">الوصف</label>
          <textarea rows={2} value={f.desc} onChange={(e) => setF({ ...f, desc: e.target.value })} data-testid="comp-desc" className="v2-input resize-none" /></div>
        <div>
          <label className="v2-label">الأيقونة</label>
          <div className="flex gap-2 flex-wrap">
            {ICON_KEYS.map((k) => {
              const I = ICONS[k];
              return (
                <button key={k} type="button" onClick={() => setF({ ...f, icon: k })} data-testid={`comp-icon-${k}`}
                  className={`w-11 h-11 rounded-2xl flex items-center justify-center border ${
                    f.icon === k ? "border-[#bb9661]/60 bg-[#bb9661]/15 text-[#e3c799]" : "border-white/10 bg-white/5 text-[#bcc8cf]"}`}>
                  <I size={19} />
                </button>
              );
            })}
          </div>
        </div>
        <div><label className="v2-label">رابط صورة (اختياري)</label>
          <input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} data-testid="comp-image" className="v2-input" dir="ltr" /></div>
        <div><label className="v2-label">الجمهور المستهدف</label>
          <AudiencePicker value={f.audience} onChange={(a) => setF({ ...f, audience: a })} testId="comp-audience" />
          <p className="mt-2 text-xs text-[#bcc8cf]">يُطبّق الاختيار على المسارات والتحديات التابعة لضمان ظهور الجدارة كاملة للفئة المحددة.</p></div>
        <div><label className="v2-label">الحالة</label>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="comp-status" className="v2-input">
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
      </div>
      <button onClick={save} disabled={busy} data-testid="comp-save" className="v2-btn w-full mt-5">حفظ</button>
    </Modal>
  );
}

export default function Competencies() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["catalog"], queryFn: async () => (await api.get("/admin/catalog")).data });
  const [modal, setModal] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["catalog"] });
  const comps = data?.competencies || [];

  const move = async (i, dir) => {
    const arr = [...comps];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    try { await api.post("/admin/competencies/reorder", { ids: arr.map((c) => c.id) }); refresh(); }
    catch (e) { toast.error(errText(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/competencies/${delTarget.id}`); toast.success("تم حذف الجدارة"); refresh(); }
    catch (e) { toast.error(errText(e)); } finally { setDelTarget(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-competencies">
      <PageHead icon={Target} title="إدارة الجدارات" sub="الجدارات هي المستوى الأعلى في رحلة التعلّم، ويتبعها المسارات التدريبية.">
        <button onClick={() => setModal({})} data-testid="comp-create" className="v2-btn v2-btn-sm"><Plus size={15} /> جدارة جديدة</button>
      </PageHead>

      {isLoading ? <Skeleton className="h-64 w-full" /> : comps.length === 0 ? <Empty text="لا توجد جدارات بعد." /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {comps.map((c, i) => {
            const I = ICONS[c.icon] || Target;
            return (
              <GlassCard key={c.id} className="p-5" data-testid={`comp-card-${c.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#91cab8]/15 text-[#91cab8] flex items-center justify-center shrink-0"><I size={23} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-extrabold">{c.name}</h3>
                      <Chip tone={c.status === "active" ? "emerald" : "muted"}>{STATUS[c.status] || c.status}</Chip>
                    </div>
                    <p className="text-xs text-[#bcc8cf] mt-1 line-clamp-2">{c.desc || "—"}</p>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <AudienceChips value={c.audience || []} />
                      <Chip tone="muted"><Layers size={11} /> {(c.paths || []).length} مسار</Chip>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
                  <button onClick={() => setModal(c)} data-testid={`comp-edit-${c.id}`} className="v2-btn-ghost v2-btn-sm"><Pencil size={13} /> تعديل</button>
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`comp-up-${c.id}`} className="v2-btn-ghost v2-btn-sm disabled:opacity-30"><ChevronUp size={13} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === comps.length - 1} data-testid={`comp-down-${c.id}`} className="v2-btn-ghost v2-btn-sm disabled:opacity-30"><ChevronDown size={13} /></button>
                  <button onClick={() => setDelTarget(c)} data-testid={`comp-del-${c.id}`} className="v2-btn-ghost v2-btn-sm text-[#F7A08A] ms-auto"><Trash2 size={13} /></button>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      {modal && <CompModal item={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={refresh} />}
      {delTarget && (
        <Confirm title="حذف الجدارة" body={`سيتم حذف «${delTarget.name}». لا يمكن حذف جدارة مرتبطة بمسارات.`}
          confirmLabel="حذف" danger onConfirm={remove} onClose={() => setDelTarget(null)} testId="comp-del-confirm" />
      )}
    </div>
  );
}
